import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import {
  InvalidProjectPathError, addFile, addFolder as addTreeFolder, applyProjectOperation, canDeleteFile, canDeleteFolder,
  canRenameFile, canRenameFolder, deleteFile as removeTreeFile, deleteFolder as removeTreeFolder, filesUnder, folderPaths,
  isManaged, isSystemFile, migrateV1Project, projectChanges, refreshRegistries, renameFile as moveTreeFile,
  renameFolder as moveTreeFolder, toV1View, treePathOfV1Path, v1PathOfTreePath, writeFile, type ProjectRefusal,
  type ProjectRefusalCode, type ProjectTree,
} from '@gotti/sandbox-core';
import type { SandboxSystems, SystemsChangedCallback } from './types';
import {
  DEFAULT_EXPANDED_FOLDERS, baseNameOf, firstFileToOpen, folderOf, foldersOf, generateNewFileCodeForPath, hasFileType,
  importEditsForRename, importersOf, isScriptPath, isTestPath, sanitizeClassName, sideOf, uniqueFilePath,
  type ImportEdit,
} from './projectPaths';

/** What the editor needs of a text model: Monaco's in the app, an in-memory one in tests. */
export interface EditorModel {
  getValue(): string;
  setValue(code: string): void;
  onDidChangeContent(listener: () => void): { dispose(): void };
  dispose(): void;
}

/** Makes the model for one file of the project. */
export type ModelFactory = (path: string, code: string) => EditorModel;

/** Hears each change to the project — the paths it changed (`ProjectChange.changed`) — and never a load. */
export type ProjectChangedCallback = (changed: string[]) => void;

/** A file of a deleted folder, with its place in the tree order — where undoing the delete puts it back. */
interface RemovedFile {
  path: string;
  code: string;
  index: number;
}

type HistoryEntry =
  /** A file added or deleted; `index` is its place in the tree order — where undoing a delete puts it back. */
  | { type: 'add'; path: string; code: string; index: number }
  | { type: 'delete'; path: string; code: string; index: number }
  | { type: 'rename'; path: string; newPath: string; importEdits: ImportEdit[] }
  | { type: 'renameFolder'; path: string; newPath: string }
  /** A folder deleted: its files, in tree order, and the empty folders that were in it. */
  | { type: 'deleteFolder'; path: string; files: RemovedFile[]; folders: string[] };

/** What the runner hears of a history entry: a folder renamed is a rename, a folder deleted a delete. */
const systemsChangeOf = (type: HistoryEntry['type']): 'add' | 'delete' | 'rename' =>
  type === 'renameFolder' ? 'rename' : type === 'deleteFolder' ? 'delete' : type;

/**
 * What a file tree action did: the path it made, renamed to or removed — or why it was refused, which the file tree shows
 * next to the name typed. A refused action changes nothing.
 */
export type TreeActionResult = { ok: true; path: string } | ProjectRefusal;

const refused = (code: ProjectRefusalCode, reason: string): ProjectRefusal => ({ ok: false, code, reason });

/** `path` moved from under `from` to under `to`; any other path as it is. */
const movedPath = (path: string, from: string, to: string): string =>
  path === from || path.startsWith(`${from}/`) ? `${to}${path.slice(from.length)}` : path;

/** How long typing in a system file rests before the registries are brought up to date with its exports. */
const REGISTRY_REFRESH_MS = 500;

/**
 * The editor's state on the project tree. The tree lives in a ref, written synchronously, and is mirrored into React
 * state for rendering — so back-to-back writes (the AI's `updateFile` calls) never race a render. Every script file has
 * a model (TypeScript checks them together); another file gets one when opened. Each model's edits flow into the tree,
 * so the tree is the project of record: saves, exports and the v1 view read it. `onProjectChanged` hears every change
 * made to it — an operation, typing, undo and redo, the AI's writes — and not a load, nor the registries catching up
 * with an edit already heard: what the save state counts as unsaved.
 */
export function useProjectEditor(
  initial: ProjectTree,
  models: MutableRefObject<Map<string, EditorModel>>,
  createModel: ModelFactory,
  onSystemsChanged?: SystemsChangedCallback,
  onProjectChanged?: ProjectChangedCallback,
) {
  const [tree, setTree] = useState(initial);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [currentPath, setCurrentPath] = useState<string | null>(() => firstFileToOpen(initial));
  const [openTabs, setOpenTabs] = useState<string[]>(() => { const first = firstFileToOpen(initial); return first ? [first] : []; });
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => new Set(DEFAULT_EXPANDED_FOLDERS));
  const [syncEpoch, setSyncEpoch] = useState(0);
  // Bumped when a load replaces every model: an editor showing a path the load kept must rebind to the new model.
  const [modelsVersion, setModelsVersion] = useState(0);

  const treeRef = useRef(initial);
  const errorsRef = useRef<Record<string, string[]>>({});
  const currentRef = useRef<string | null>(firstFileToOpen(initial));
  const onChangedRef = useRef(onSystemsChanged);
  onChangedRef.current = onSystemsChanged;
  const onProjectChangedRef = useRef(onProjectChanged);
  onProjectChangedRef.current = onProjectChanged;
  const createModelRef = useRef(createModel);
  createModelRef.current = createModel;

  const ops = useMemo(() => {
    const undoStack: HistoryEntry[] = [];
    let redoStack: HistoryEntry[] = [];
    let clipboard: { path: string; code: string; isCut: boolean } | null = null;
    const subscriptions = new Map<string, { dispose(): void }>();
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;

    const bumpSyncEpoch = () => setSyncEpoch((epoch) => epoch + 1);
    const commit = (next: ProjectTree) => { treeRef.current = next; setTree(next); };
    /** Shows `path` in the editor — with a model: a file other than a script file gets one only when it is shown. */
    const setCurrent = (path: string | null) => {
      if (path !== null && Object.hasOwn(treeRef.current.files, path) && !models.current.has(path)) attach(path, treeRef.current.files[path]);
      currentRef.current = path;
      setCurrentPath(path);
    };
    const setFileErrors = (path: string, fileErrors: string[]) => {
      errorsRef.current = { ...errorsRef.current, [path]: fileErrors };
      setErrors(errorsRef.current);
    };

    /** Drops a registry refresh still waiting for typing to rest — when the editor goes away. */
    const cancelRegistryRefresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = null;
    };
    const scheduleRegistryRefresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        const fresh = refreshRegistries(treeRef.current);
        if (fresh !== treeRef.current) apply(fresh);
      }, REGISTRY_REFRESH_MS);
    };

    const attach = (path: string, code: string) => {
      const model = createModelRef.current(path, code);
      models.current.set(path, model);
      subscriptions.set(path, model.onDidChangeContent(() => {
        const current = treeRef.current;
        if (!Object.hasOwn(current.files, path)) return;
        const value = model.getValue();
        if (current.files[path] === value) return;
        commit(writeFile(current, path, value));
        onProjectChangedRef.current?.([path]);
        if (isSystemFile(path)) scheduleRegistryRefresh();
      }));
    };
    const release = (path: string) => {
      subscriptions.get(path)?.dispose();
      subscriptions.delete(path);
      models.current.get(path)?.dispose();
      models.current.delete(path);
    };

    /** Makes `next` — the project after a change made to it — the project, and says what changed. */
    function change(next: ProjectTree) {
      const before = treeRef.current;
      apply(next);
      const changed = projectChanges(before, next);
      if (changed.length) onProjectChangedRef.current?.(changed);
    }

    /** Makes `next` the project; the models follow it — a removed file's released, a new script file's made, a changed one updated. */
    function apply(next: ProjectTree) {
      commit(next);
      for (const path of [...models.current.keys()]) if (!Object.hasOwn(next.files, path)) release(path);
      // A file that is gone takes its errors with it; a renamed one's are reported again for its new path.
      if (Object.keys(errorsRef.current).some((path) => !Object.hasOwn(next.files, path))) {
        errorsRef.current = Object.fromEntries(Object.entries(errorsRef.current).filter(([path]) => Object.hasOwn(next.files, path)));
        setErrors(errorsRef.current);
      }
      for (const [path, code] of Object.entries(next.files)) {
        const model = models.current.get(path);
        if (model) {
          if (model.getValue() !== code) model.setValue(code);
        } else if (isScriptPath(path)) {
          attach(path, code);
        }
      }
    }

    const expandTo = (folder: string) => {
      if (!folder) return;
      setExpandedFolders((previous) => new Set([...previous, ...foldersOf(`${folder}/x`)]));
    };
    const open = (path: string) => {
      if (!Object.hasOwn(treeRef.current.files, path)) return;
      if (!models.current.has(path)) attach(path, treeRef.current.files[path]);
      setCurrent(path);
      setOpenTabs((tabs) => (tabs.includes(path) ? tabs : [...tabs, path]));
    };
    const replaceOpen = (from: string, to: string) => {
      setOpenTabs((tabs) => tabs.map((tab) => (tab === from ? to : tab)));
      if (currentRef.current === from) setCurrent(to);
    };
    /** Closes every one of `paths` that is open; when the current file is among them, shows `instead`. */
    const closeAll = (paths: readonly string[], instead: string | null) => {
      const gone = new Set(paths);
      const wasCurrent = currentRef.current !== null && gone.has(currentRef.current);
      setOpenTabs((tabs) => {
        const rest = tabs.filter((tab) => !gone.has(tab));
        return wasCurrent && instead && !rest.includes(instead) ? [...rest, instead] : rest;
      });
      if (wasCurrent) setCurrent(instead);
    };
    /** Closes `path` wherever it is open; when it was the current file, shows `instead`. */
    const closeOpen = (path: string, instead: string | null) => closeAll([path], instead);
    /** The file to show when `path` goes: the next one in its folder, else the one before, else the first to open. */
    const neighbourOf = (before: ProjectTree, path: string, after: ProjectTree): string | null => {
      const siblings = Object.keys(before.files).filter((key) => folderOf(key) === folderOf(path));
      const at = siblings.indexOf(path);
      return siblings[at + 1] ?? siblings[at - 1] ?? firstFileToOpen(after);
    };

    /** Makes `next` — the project with a new file at `path` — the project: in the history, its folder opened, the file shown. */
    const created = (path: string, next: ProjectTree) => {
      change(next);
      undoStack.push({ type: 'add', path, code: next.files[path], index: Object.keys(next.files).indexOf(path) });
      redoStack = [];
      onChangedRef.current?.('add', sideOf(path));
      expandTo(folderOf(path));
      open(path);
      bumpSyncEpoch();
    };
    /** A new file the project's own rules do not name: a pasted file keeps the name it had, the AI writes where it writes. */
    const create = (path: string, code: string): string => {
      created(path, addFile(treeRef.current, path, code));
      return path;
    };
    const has = (path: string) => Object.hasOwn(treeRef.current.files, path);
    const hasFolder = (tree: ProjectTree, folder: string) => folderPaths(tree).includes(folder);
    const canDelete = (path: string) => canDeleteFile(treeRef.current, path);
    const canRename = (path: string) => canRenameFile(treeRef.current, path);
    const isReadOnly = (path: string) => isManaged(treeRef.current, path);

    /** Runs a tree change a person's input asked for; a path the tree refuses is reported and leaves the project as it was. */
    const attempt = <T>(run: () => T, refused: T): T => {
      try {
        return run();
      } catch (err) {
        if (!(err instanceof InvalidProjectPathError)) throw err;
        console.warn(`[SandboxEditor] ${err.message}`);
        return refused;
      }
    };

    const deleteFile = (path: string): TreeActionResult => {
      const before = treeRef.current;
      const result = applyProjectOperation(before, { kind: 'deleteFile', path });
      if (!result.ok) return result;
      undoStack.push({ type: 'delete', path, code: before.files[path], index: Object.keys(before.files).indexOf(path) });
      redoStack = [];
      onChangedRef.current?.('delete', sideOf(path));
      change(result.tree);
      closeOpen(path, neighbourOf(before, path, treeRef.current));
      bumpSyncEpoch();
      return { ok: true, path };
    };

    /**
     * The rename a person typed for `path`, tried on the project as it is: the name keeps the file's extension unless it
     * gives one, and a file never becomes a test file — the file tree does not show those, and a system would leave its
     * registry. The project after it (with its importers rewritten when asked), or why it was refused.
     */
    const planRename = (path: string, typed: string, updateImports: boolean): { ok: true; target: string; next: ProjectTree; importEdits: ImportEdit[] } | ProjectRefusal => {
      const name = typed.trim();
      if (!name) return refused('invalid_name', 'a file needs a name');
      const extension = hasFileType(name) ? '' : (/\.[A-Za-z0-9]+$/.exec(baseNameOf(path))?.[0] ?? '');
      const target = folderOf(path) ? `${folderOf(path)}/${name}${extension}` : `${name}${extension}`;
      const before = treeRef.current;
      if (target === path) return { ok: true, target, next: before, importEdits: [] };
      if (isTestPath(target) && !isTestPath(path)) return refused('invalid_name', `${baseNameOf(target)} would be a test file, which the file tree does not show`);
      const moved = applyProjectOperation(before, { kind: 'renameFile', from: path, to: target });
      if (!moved.ok) return moved;
      const importEdits = updateImports ? importEditsForRename(before, path, target) : [];
      let next = moved.tree;
      for (const edit of importEdits) {
        const written = applyProjectOperation(next, { kind: 'writeFile', path: edit.path, content: edit.next });
        if (!written.ok) throw new Error(`[SandboxEditor] rewriting the imports of ${edit.path} was refused: ${written.reason}`);
        next = written.tree;
      }
      return { ok: true, target, next, importEdits };
    };

    const renameFile = (path: string, newName: string, options?: { updateImports?: boolean }): TreeActionResult => {
      const plan = planRename(path, newName, options?.updateImports ?? false);
      if (!plan.ok) return plan;
      if (plan.target === path) return { ok: true, path };
      undoStack.push({ type: 'rename', path, newPath: plan.target, importEdits: plan.importEdits });
      redoStack = [];
      change(plan.next);
      replaceOpen(path, plan.target);
      onChangedRef.current?.('rename', sideOf(path));
      bumpSyncEpoch();
      return { ok: true, path: plan.target };
    };

    /** Makes `next` — the project with the folder `from` at `to` — the project; open files, the current one and open folders follow. */
    const relocateFolder = (from: string, to: string, next: ProjectTree) => {
      change(next);
      setOpenTabs((tabs) => tabs.map((tab) => movedPath(tab, from, to)));
      if (currentRef.current !== null && movedPath(currentRef.current, from, to) !== currentRef.current) setCurrent(movedPath(currentRef.current, from, to));
      setExpandedFolders((previous) => new Set([...previous].map((folder) => movedPath(folder, from, to))));
    };

    const renameFolder = (folder: string, newName: string): TreeActionResult => {
      const name = newName.trim();
      if (!name) return refused('invalid_name', 'a folder needs a name');
      const target = folderOf(folder) ? `${folderOf(folder)}/${name}` : name;
      if (target === folder) return { ok: true, path: folder };
      const result = applyProjectOperation(treeRef.current, { kind: 'renameFolder', from: folder, to: target });
      if (!result.ok) return result;
      undoStack.push({ type: 'renameFolder', path: folder, newPath: target });
      redoStack = [];
      relocateFolder(folder, target, result.tree);
      onChangedRef.current?.('rename', sideOf(folder));
      bumpSyncEpoch();
      return { ok: true, path: target };
    };

    /** Makes `next` — the project without the folder whose files were `removed` — the project; their tabs close. */
    const dropFolder = (removed: readonly string[], next: ProjectTree) => {
      change(next);
      closeAll(removed, firstFileToOpen(next));
    };

    const deleteFolder = (folder: string): TreeActionResult => {
      const before = treeRef.current;
      const result = applyProjectOperation(before, { kind: 'deleteFolder', path: folder });
      if (!result.ok) return result;
      const order = Object.keys(before.files);
      const files = filesUnder(before, folder).map((path) => ({ path, code: before.files[path], index: order.indexOf(path) }));
      const folders = before.folders.filter((each) => each === folder || each.startsWith(`${folder}/`));
      undoStack.push({ type: 'deleteFolder', path: folder, files, folders });
      redoStack = [];
      onChangedRef.current?.('delete', sideOf(folder));
      dropFolder(files.map((file) => file.path), result.tree);
      bumpSyncEpoch();
      return { ok: true, path: folder };
    };

    /** `tree` with a deleted folder back: its files in their places in the tree order, and its empty folders. */
    const restoreFolder = (tree: ProjectTree, files: readonly RemovedFile[], folders: readonly string[]): ProjectTree => {
      let next = tree;
      for (const file of files) if (!Object.hasOwn(next.files, file.path)) next = addFile(next, file.path, file.code, file.index);
      for (const folder of folders) if (!hasFolder(next, folder)) next = addTreeFolder(next, folder);
      return next;
    };

    const undo = () => {
      const entry = undoStack.pop();
      if (!entry) return;
      const current = treeRef.current;
      if (entry.type === 'add') {
        if (!Object.hasOwn(current.files, entry.path)) return;
        change(removeTreeFile(current, entry.path));
        closeOpen(entry.path, firstFileToOpen(treeRef.current));
      } else if (entry.type === 'delete') {
        if (Object.hasOwn(current.files, entry.path)) return;
        change(addFile(current, entry.path, entry.code, entry.index));
      } else if (entry.type === 'rename') {
        if (!Object.hasOwn(current.files, entry.newPath)) return;
        let next = moveTreeFile(current, entry.newPath, entry.path);
        for (const edit of entry.importEdits) if (Object.hasOwn(next.files, edit.path)) next = writeFile(next, edit.path, edit.previous);
        change(next);
        replaceOpen(entry.newPath, entry.path);
      } else if (entry.type === 'renameFolder') {
        if (!hasFolder(current, entry.newPath)) return;
        if (!attempt(() => { relocateFolder(entry.newPath, entry.path, moveTreeFolder(current, entry.newPath, entry.path)); return true; }, false)) return;
      } else {
        if (hasFolder(current, entry.path)) return;
        if (!attempt(() => { change(restoreFolder(current, entry.files, entry.folders)); return true; }, false)) return;
      }
      redoStack.push(entry);
      onChangedRef.current?.(systemsChangeOf(entry.type), sideOf(entry.path));
      bumpSyncEpoch();
    };

    const redo = () => {
      const entry = redoStack.pop();
      if (!entry) return;
      const current = treeRef.current;
      if (entry.type === 'add') {
        if (Object.hasOwn(current.files, entry.path)) return;
        change(addFile(current, entry.path, entry.code, entry.index));
      } else if (entry.type === 'delete') {
        if (!Object.hasOwn(current.files, entry.path)) return;
        change(removeTreeFile(current, entry.path));
        closeOpen(entry.path, firstFileToOpen(treeRef.current));
      } else if (entry.type === 'rename') {
        if (!Object.hasOwn(current.files, entry.path)) return;
        let next = moveTreeFile(current, entry.path, entry.newPath);
        for (const edit of entry.importEdits) if (Object.hasOwn(next.files, edit.path)) next = writeFile(next, edit.path, edit.next);
        change(next);
        replaceOpen(entry.path, entry.newPath);
      } else if (entry.type === 'renameFolder') {
        if (!hasFolder(current, entry.path)) return;
        if (!attempt(() => { relocateFolder(entry.path, entry.newPath, moveTreeFolder(current, entry.path, entry.newPath)); return true; }, false)) return;
      } else {
        if (!hasFolder(current, entry.path)) return;
        if (!attempt(() => { dropFolder(filesUnder(current, entry.path), removeTreeFolder(current, entry.path)); return true; }, false)) return;
      }
      undoStack.push(entry);
      onChangedRef.current?.(systemsChangeOf(entry.type), sideOf(entry.path));
      bumpSyncEpoch();
    };

    const loadProject = (next: ProjectTree) => {
      for (const path of [...models.current.keys()]) release(path);
      commit(next);
      for (const [path, code] of Object.entries(next.files)) if (isScriptPath(path)) attach(path, code);
      setModelsVersion((version) => version + 1);
      const first = firstFileToOpen(next);
      setCurrent(first);
      setOpenTabs(first ? [first] : []);
      errorsRef.current = {};
      setErrors({});
      undoStack.length = 0;
      redoStack = [];
      bumpSyncEpoch();
    };

    return {
      bumpSyncEpoch,
      /** Makes the models of the tree's script files — once Monaco is configured. */
      mountModels: () => { for (const [path, code] of Object.entries(treeRef.current.files)) if (isScriptPath(path) && !models.current.has(path)) attach(path, code); },
      /** Releases every model — when the editor unmounts. */
      releaseModels: () => { cancelRegistryRefresh(); for (const path of [...models.current.keys()]) release(path); },
      cancelRegistryRefresh,
      open,
      /** Closes a tab — never the last one; closing the current tab shows the tab that took its place. */
      closeTab: (path: string, tabs: readonly string[]) => {
        if (tabs.length <= 1 || !tabs.includes(path)) return;
        const at = tabs.indexOf(path);
        const rest = tabs.filter((tab) => tab !== path);
        setOpenTabs(rest);
        if (currentRef.current === path) setCurrent(rest[Math.min(at, rest.length - 1)] ?? null);
      },
      toggleFolder: (folder: string) => setExpandedFolders((previous) => {
        const next = new Set(previous);
        if (next.has(folder)) next.delete(folder); else next.add(folder);
        return next;
      }),
      setFileErrors,
      /** A file a person names in a folder: no extension means `.ts`, sanitized as before; a name with an extension must be valid. */
      /**
       * A file a person names in a folder: a name with no file type gets `.ts`, sanitized to a class name as before, and a
       * name taken in any letter case gets the next free number (`Input1.ts`) — then the `createFile` operation, which
       * refuses an invalid name with its reason.
       */
      newFile: (folder: string, typed: string): TreeActionResult => {
        const trimmed = typed.trim();
        if (!trimmed) return refused('invalid_name', 'a file needs a name');
        const name = hasFileType(trimmed) ? trimmed : `${sanitizeClassName(trimmed)}.ts`;
        const path = uniqueFilePath(treeRef.current, folder, name);
        const result = applyProjectOperation(treeRef.current, { kind: 'createFile', path, content: generateNewFileCodeForPath(path) });
        if (!result.ok) return result;
        created(path, result.tree);
        return { ok: true, path };
      },
      /** A folder a person names inside `parent` — letters, digits, `_` and `-` kept, as before — by the `createFolder` operation. */
      newFolder: (parent: string, typed: string): TreeActionResult => {
        const trimmed = typed.trim();
        const name = trimmed.replace(/[^a-zA-Z0-9_-]/g, '');
        if (!name) return refused('invalid_name', trimmed ? `"${trimmed}" has no letter, digit, _ or - to name a folder with` : 'a folder needs a name');
        const path = parent ? `${parent}/${name}` : name;
        const result = applyProjectOperation(treeRef.current, { kind: 'createFolder', path });
        if (!result.ok) return result;
        change(result.tree);
        expandTo(path);
        bumpSyncEpoch();
        return { ok: true, path };
      },
      /** v1 adapter (`window.sandboxEditor.addNewFile`): a file in `<folder>/<subfolder>`, named as the editor always named one. */
      addNewFile: (folder: 'client' | 'server' | 'shared', subfolder: string | null, name: string): string | null => {
        const parent = subfolder === null ? `src/${folder}` : `src/${folder}/${subfolder}`;
        const path = uniqueFilePath(treeRef.current, parent, `${sanitizeClassName(name)}.ts`);
        const result = applyProjectOperation(treeRef.current, { kind: 'createFile', path, content: generateNewFileCodeForPath(path) });
        if (!result.ok) {
          console.warn(`[SandboxEditor] ${path}: ${result.reason}`);
          return null;
        }
        created(path, result.tree);
        return path;
      },
      /** v1 adapter (`window.sandboxEditor.addFolder`): a subfolder of a side; false when it exists or the name is empty. */
      addFolder: (folder: 'client' | 'server' | 'shared', subfolderName: string): boolean => {
        const name = subfolderName.trim().replace(/[^a-zA-Z0-9_-]/g, '');
        if (!name) return false;
        const path = `src/${folder}/${name}`;
        const result = applyProjectOperation(treeRef.current, { kind: 'createFolder', path });
        if (!result.ok) {
          console.warn(`[SandboxEditor] ${path}: ${result.reason}`);
          return false;
        }
        change(result.tree);
        expandTo(path);
        bumpSyncEpoch();
        return true;
      },
      deleteFile,
      renameFile,
      /** Where renaming `path` to `typed` would take it, or why it may not — without renaming it (the file tree asks before it offers to rewrite imports). */
      checkRename: (path: string, typed: string): TreeActionResult => {
        const plan = planRename(path, typed, false);
        return plan.ok ? { ok: true, path: plan.target } : plan;
      },
      renameFolder,
      deleteFolder,
      copyFile: (path: string): boolean => {
        if (!has(path)) return false;
        clipboard = { path, code: treeRef.current.files[path], isCut: false };
        return true;
      },
      cutFile: (path: string): boolean => {
        if (!canDelete(path)) return false;
        // As before: never the last file of a folder.
        if (Object.keys(treeRef.current.files).filter((key) => folderOf(key) === folderOf(path)).length <= 1) return false;
        clipboard = { path, code: treeRef.current.files[path], isCut: true };
        deleteFile(path);
        return true;
      },
      pasteFile: (folder: string): string | null => {
        const clip = clipboard;
        if (!clip) return null;
        const path = uniqueFilePath(treeRef.current, folder, baseNameOf(clip.path));
        const pasted = attempt(() => create(path, clip.code), null);
        if (pasted !== null && clip.isCut) clipboard = null;
        return pasted;
      },
      hasClipboard: () => clipboard !== null,
      hasImportReferences: (path: string) => importersOf(treeRef.current, path).length > 0,
      canDelete,
      canRename,
      canDeleteFolder: (folder: string) => canDeleteFolder(treeRef.current, folder),
      canRenameFolder: (folder: string) => canRenameFolder(treeRef.current, folder),
      isReadOnly,
      undo,
      redo,
      loadProject,
      /** v1 adapter (`window.sandboxEditor.loadSystems`, the templates): a v1 project, migrated to a tree. */
      loadSystems: (systems: SandboxSystems, name = 'sandbox') => loadProject(migrateV1Project(systems, { name })),
      /** The project as it is now, its registries brought up to date first — what a save, fork or export sends. */
      getProject: (): ProjectTree => {
        const fresh = refreshRegistries(treeRef.current);
        if (fresh !== treeRef.current) apply(fresh);
        return treeRef.current;
      },
      /** v1 adapter: the project's v1 view — empty folders included — with each file's errors, as the runner and the AI read it. */
      getAllSystems: (): SandboxSystems => {
        const current = treeRef.current;
        const view = toV1View(current.files, current.generated, current.folders) as SandboxSystems;
        for (const folder of ['client', 'server', 'shared'] as const) {
          const mark = (file: { name: string; errors?: string[] }, path: string) => {
            const fileErrors = errorsRef.current[path];
            if (fileErrors?.length) file.errors = fileErrors;
          };
          for (const file of view[folder].rootFiles) mark(file, `src/${folder}/${file.name}.ts`);
          for (const [subfolder, files] of Object.entries(view[folder].subfolders)) {
            for (const file of files) mark(file, `src/${folder}/${subfolder}/${file.name}.ts`);
          }
        }
        return view;
      },
      /** Every file the AI sees, at its v1 path (`client/…`, `__tests__/…`), in tree order — generated files left out. */
      getAiFiles: (): { path: string; content: string }[] => {
        const current = treeRef.current;
        return Object.entries(current.files).flatMap(([path, content]) => {
          const v1Path = current.generated.has(path) ? null : v1PathOfTreePath(path);
          return v1Path === null ? [] : [{ path: v1Path, content }];
        });
      },
      /** v1 adapter (the AI apply path, e2e): writes `client/…`, `server/…`, `shared/…` or `__tests__/…`, creating the file when it is new. */
      updateFile: (v1Path: string, content: string): boolean => {
        const path = treePathOfV1Path(v1Path);
        if (path === null) return false;
        // The AI never sees the generated topology (getAiFiles leaves it out), so a write onto one of its paths is refused.
        if (treeRef.current.generated.has(path)) {
          console.warn(`[SandboxEditor] ${v1Path} is generated by the site; the AI never writes it.`);
          return false;
        }
        return attempt(() => {
          // The exact path: a v1 project can hold two files whose names differ only in case. A new spelling of an
          // existing name is a new file, which the tree refuses, naming the file it would collide with.
          const current = treeRef.current;
          if (!Object.hasOwn(current.files, path)) {
            create(path, content);
          } else {
            change(writeFile(current, path, content));
            if (isSystemFile(path)) scheduleRegistryRefresh();
          }
          return true;
        }, false);
      },
    };
  }, [models]);

  // A refresh still pending when the editor unmounts would write the tree of an editor nothing shows any more.
  useEffect(() => ops.cancelRegistryRefresh, [ops]);

  return { tree, errors, currentPath, openTabs, expandedFolders, syncEpoch, modelsVersion, ...ops };
}
